import { useCallback, useEffect, useRef, useState } from 'react';
import { apiEndpoints, apiPublic } from '../../../utils/apiService';
import { type Confirmation, draftStateOf } from './link';
import type { IntakeDraft, StepKey } from './model';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Wait this long after the last change before saving. */
const SAVE_DELAY_MS = 800;
/** After a failed save, try again this long after. */
const RETRY_MS = 5000;

const snapshotOf = (d: IntakeDraft) =>
  JSON.stringify({ ...d, updatedAt: null });

export interface SaveResponse {
  saved_at: string;
  confirmed_steps: Partial<Record<StepKey, Confirmation>>;
}

/**
 * The draft of a shared intake link, saved to the server. A change saves
 * 800ms after typing stops (held while `pause`, an address lookup in flight,
 * like the device draft). `save(extra)` sends next, with a step confirmation
 * when there is one; it rejects if the server didn't take it. Saves run one
 * after another, never side by side.
 */
export function useLinkDraft(
  token: string,
  initial: () => IntakeDraft,
  pause = false,
) {
  const [draft, setDraft] = useState<IntakeDraft>(initial);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [retry, setRetry] = useState(0);
  const lastSaved = useRef(snapshotOf(draft));
  const latest = useRef(draft);
  latest.current = draft;

  // One save at a time: each sends the draft as it is when its turn comes,
  // so an older request can't land after a newer one and win.
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const send = useCallback(
    async (extra?: Record<string, unknown>): Promise<SaveResponse> => {
      const d = latest.current;
      const snapshot = snapshotOf(d);
      setSaveState('saving');
      const res = await apiPublic
        .patch(apiEndpoints.publicOnboardingLink(token), {
          payload: d.payload,
          draft_state: draftStateOf(d),
          ...extra,
        })
        .catch(() => null);
      if (!res || !res.ok) {
        setSaveState('error');
        throw new Error(res ? `Save failed (${res.status})` : 'Offline');
      }
      lastSaved.current = snapshot;
      setSavedAt(Date.now());
      setSaveState(
        snapshotOf(latest.current) === snapshot ? 'saved' : 'saving',
      );
      return (await res.json()) as SaveResponse;
    },
    [token],
  );

  const save = useCallback(
    (extra?: Record<string, unknown>): Promise<SaveResponse> => {
      const run = queue.current.catch(() => undefined).then(() => send(extra));
      queue.current = run;
      return run;
    },
    [send],
  );

  useEffect(() => {
    if (pause) return;
    if (snapshotOf(draft) === lastSaved.current) return;
    const t = window.setTimeout(() => {
      save().catch(() => {
        window.setTimeout(() => setRetry((n) => n + 1), RETRY_MS);
      });
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [draft, pause, save, retry]);

  const update = useCallback(
    (fn: (d: IntakeDraft) => IntakeDraft) => setDraft(fn),
    [],
  );

  return { draft, update, save, saveState, savedAt };
}
